import * as d3 from "d3";
import { extractMessage, callGqlApi } from "../../helpers/api";
import { snackbarError } from "../snackbar/snackbar-actions";
import { Thunk } from "../common";

import { parseISOTimeUTC, HOUR_IN_MS } from "../../helpers/time";

import {
  RxSeriesType,
  RxDiagAction,
  RxEvent,
  RxSeries,
  RxSeriesDatum,
  RxEventType,
  RxDataType,
} from "./rx-diag-types";
import { SnackBarAction } from "../snackbar/snackbar-types";

import gql from "../gqlTag";
import { selectAvailableEventTypes, selectAvailableSeriesTypes } from "./rxdiag-selectors";

// used to determine the duration of the count period from the column header in the CSV
export const COUNT_PERIOD_LOOKUP: { [yHeader: string]: number | undefined } = {
  Dets_1H: HOUR_IN_MS,
  Pings_1H: HOUR_IN_MS,
  Pings_24H: HOUR_IN_MS * 24,
};

export function loadDataAsNeeded(): Thunk<void, RxDiagAction> {
  return dispatch => {
    dispatch(loadSeriesAsNeeded());
    dispatch(loadEventsAsNeeded());
  };
}

/* Update the selection of log files.
 *
 * For each file:
 *   - If it's the first time the file has been selected, its datalist is fetched from the server,
 *     which tells us data types are available for that file.
 *   - If there are any series types selected they will be automatically fetched, if not already, and if available.
 *   - If there are any event types selected (e.g. "OFFLOAD"), the events will be fetched, if not already, and if available. */
export function selectFiles(filenames: string[]): Thunk<Promise<void>, RxDiagAction> {
  return async (dispatch, getState) => {
    const state = getState();
    dispatch({ type: "RXDIAG_SELECT_FILES", payload: { filenames } });

    // Filter down selected series and event types to those that are still available
    dispatch(filterSelectedDataTypes());

    for (const filename of filenames) {
      const status = state.rxdiag.dataListByFile[filename];
      if (!status) {
        // first time file has been selected
        await dispatch(readDataList(filename));
      }
    }

    dispatch(loadDataAsNeeded());
  };
}

/* Update the selection of data series types (e.g. "noise" or "tilt")

 * For each selected file:
 *   - If there are any series types selected they will be automatically fetched, if not already, if available. */
export function selectSeriesTypes(seriesTypes: RxSeriesType[]): Thunk<void, RxDiagAction> {
  return dispatch => {
    dispatch({ type: "RXDIAG_SELECT_SERIES_TYPES", payload: { seriesTypes } });
    dispatch(loadSeriesAsNeeded());
  };
}

/* Handle the event types that the user has selected in the RxDiag app.
 * For each selected file:
 *   - If there are any events selected, the events will be fetched, if not already, if available. */
export function selectEventTypes(eventTypes: RxEventType[]): Thunk<void, RxDiagAction> {
  return (dispatch, getState) => {
    dispatch({ type: "RXDIAG_SELECT_EVENT_TYPES", payload: { eventTypes } });

    if (eventTypes.length === 0) return;

    const { selectedFiles, dataListByFile, eventsData } = getState().rxdiag;
    for (const filename of selectedFiles) {
      const dataList = dataListByFile[filename];
      if (dataList?.status === "loaded" && dataList.eventsAvailable) {
        // events are avaiable for this file, now check if we already loaded them
        const status = eventsData.find(d => d.filename === filename)?.status;
        if (status !== "loaded") {
          // first time events have been selected for this file
          dispatch(loadEvents(filename));
        }
      }
    }
  };
}

// HELPER ACTIONS

// load any missing series
function loadSeriesAsNeeded(): Thunk<void, RxDiagAction> {
  return (dispatch, getState) => {
    const timeC = getState().user.useTimeCorrection;
    const { selectedFiles, selectedSeriesTypes, seriesData, dataListByFile } = getState().rxdiag;
    for (const filename of selectedFiles) {
      const dataList = dataListByFile[filename];
      if (dataList?.status !== "loaded") continue;

      for (const type of selectedSeriesTypes) {
        if (dataList.seriesTypes.includes(type)) {
          // this data type is available for this file, now check if we have already loaded it
          const loadingOrLoaded = seriesData.some(
            d => d.filename === filename && d.type === type && d.timeC === timeC
          );
          if (!loadingOrLoaded) {
            // No entry in seriesData means the file is neither loading nor loaded, start loading it now
            dispatch(loadSeries(filename, type, timeC));
          }
        }
      }
    }
  };
}

// load any missing events
function loadEventsAsNeeded(): Thunk<void, RxDiagAction> {
  return (dispatch, getState) => {
    const { rxdiag, user } = getState();
    const { selectedFiles, selectedEventTypes, eventsData } = rxdiag;
    const timeC = user.useTimeCorrection;
    if (selectedEventTypes.length === 0) return;

    for (const filename of selectedFiles) {
      if (!eventsData.some(d => d.filename === filename && d.timeC === timeC)) {
        // first time events have been selected for this file: load them
        dispatch(loadEvents(filename));
      }
    }
  };
}

// Update selected series and event types, based on the new file selection.
// This is only relevant if files are unselected: we want to avoid leaving
// unavaible series and event types selected.
function filterSelectedDataTypes(): Thunk<void, RxDiagAction> {
  return (dispatch, getState) => {
    const state = getState();
    const availEventTypes = selectAvailableEventTypes(state);
    const availSeriesTypes = selectAvailableSeriesTypes(state);
    const seriesTypes = state.rxdiag.selectedSeriesTypes.filter(t => availSeriesTypes.includes(t));
    const eventTypes = state.rxdiag.selectedEventTypes.filter(t => availEventTypes.includes(t));
    dispatch({ type: "RXDIAG_SELECT_SERIES_TYPES", payload: { seriesTypes } });
    dispatch({ type: "RXDIAG_SELECT_EVENT_TYPES", payload: { eventTypes } });
  };
}

// DATA LIST

// Fetch and parse the datalist for a given file.
function readDataList(filename: string): Thunk<Promise<void>, RxDiagAction | SnackBarAction> {
  return async (dispatch, state) => {
    dispatch({ type: "RXDIAG_DATALIST_LOADING", payload: { filename } });

    try {
      const fileId: string = state().files.fileList.find(f => f.name === filename)?.id || "";
      const availableTypes = await fetchAvailableTypesGQL(fileId);
      dispatch({
        type: "RXDIAG_DATALIST_LOADED",
        payload: { filename, dataTypes: availableTypes },
      });
    } catch (error) {
      dispatch(snackbarError(extractMessage(error)));
    }
  };
}

// SERIES DATA

// Fetch series data for a given file & type
function loadSeries(
  filename: string,
  seriesType: RxSeriesType,
  timeC: boolean
): Thunk<void, RxDiagAction | SnackBarAction> {
  return (dispatch, state) => {
    dispatch({ type: "RXDIAG_SERIES_LOADING", payload: { filename, seriesType, timeC } });

    // Get serial per file from existing state. Note: this assumes the file data is already
    // populated. In current usage, this has to be so because the data for a file cannot be
    // requested until the files are known. However, this may not always be so.
    const serialByFile = {};
    state().files.fileList?.forEach(file => {
      if (file.rxLogProperties?.serial) {
        serialByFile[file.name] = file.rxLogProperties.serial;
      }
    });
    const fileId: string = state().files.fileList.find(f => f.name === filename)?.id || "";

    fetchLogDataGQL(fileId, seriesType, timeC)
      .then(res => parseSeries(res.data, seriesType))
      .then(({ yVariable, data }) => {
        const series = {
          filename,
          type: seriesType,
          yVariable,
          data,
          serial: serialByFile[filename],
        };
        dispatch({
          type: "RXDIAG_SERIES_LOADED",
          payload: { series, timeC },
        });
      })
      .catch(error => {
        console.error(error);
        dispatch(snackbarError(extractMessage(error)));
      });
  };
}

// Parse the csv data for a given series type
function parseSeries(csv: string, dataType: RxSeriesType): Pick<RxSeries, "yVariable" | "data"> {
  const csvData = d3.csvParse(csv);
  const yColName = Object.keys(csvData[0])[1];

  const countPeriod = COUNT_PERIOD_LOOKUP[yColName];
  const yVariable = adjustYHeader(yColName);

  let data = csvData.map(d => ({
    x: parseISOTimeUTC(d.Time as string),
    y: +(d[yColName] as string),
  }));

  if (countPeriod) {
    if (dataType === "pings") {
      // ping counts apply to the period _before_ the time: adjust the time values accordingly
      data.forEach(d => {
        d.x = new Date(d.x.getTime() - countPeriod);
      });
    }
    /* If the data is counted in bins, insert records with zero counts at the start of any gaps in the data
     * This ensures that the values on the charts read zero where there is no data. */
    data = insertZeroCounts(data, countPeriod);

    // Makes sure a line is displayed when there is only data point
    if (data.length === 1) {
      data.push({ x: new Date(data[0].x.getTime() + countPeriod), y: data[0].y });
    }
  }

  return { yVariable, data };
}

// insert records with zero counts at the start of any gaps in the data
function insertZeroCounts(data: RxSeriesDatum[], countPeriod: number): RxSeriesDatum[] {
  const result: RxSeriesDatum[] = [];
  const threshold = countPeriod * 1.1;

  for (let i = 0; i < data.length; i++) {
    result.push(data[i]);
    if (i < data.length - 2) {
      const diff = data[i + 1].x.getTime() - data[i].x.getTime();
      if (diff > threshold) {
        result.push({ x: new Date(data[i].x.getTime() + countPeriod), y: 0 });
      }
    }
  }

  return result;
}

// adhoc adjustments to yVariable names
function adjustYHeader(yColName: string): string {
  switch (yColName) {
    case "Dets_1H":
      return "Dets. / Hour";
    case "Pings_1H":
      return "Pings / Hour";
    case "Pings_24H":
      return "Pings / Day";
    default:
      return yColName.replace("Temperature", "Temp.").replace(/\s*deg\s*/, "°");
  }
}

// Parse the csv response for a file's series type
function loadEvents(filename: string): Thunk<void, RxDiagAction | SnackBarAction> {
  return (dispatch, state) => {
    const { user, files } = state();
    const timeC = user.useTimeCorrection;
    dispatch({ type: "RXDIAG_EVENTS_LOADING", payload: { filename, timeC } });

    const fileId: string = files.fileList.find(f => f.name === filename)?.id || "";
    fetchLogDataGQL(fileId, "events", timeC)
      .then(parseEvents)
      .then(events =>
        dispatch({ type: "RXDIAG_EVENTS_LOADED", payload: { filename, timeC, events } })
      )
      .catch(error => {
        console.error(error);
        dispatch(snackbarError(extractMessage(error)));
      });
  };
}

// Parse the csv reponse for a file's event data
function parseEvents(response: any): Omit<RxEvent, "filename">[] {
  const events = d3.csvParse(response.data);
  return events.map(d => {
    return {
      time: parseISOTimeUTC(d.Time as string),
      type: d.Event as RxEventType,
    };
  });
}

// API HELPERS

async function fetchAvailableTypesGQL(fileId: string): Promise<RxDataType[]> {
  const availableQuery = gql`
    query availableRxFileDiagnostics($fileId: String!) {
      availableRxFileDiagnostics(fileId: $fileId)
    }
  `;
  const res = await callGqlApi(availableQuery, { fileId });
  return res.availableRxFileDiagnostics;
}

async function fetchLogDataGQL(
  fileId: string,
  dataType: RxSeriesType | "events",
  correctedTime: boolean
) {
  const query = gql`
    query rxFileDiagnostic($fileId: String!, $type: RxDiagnosticTypes!, $correctedTime: Boolean!) {
      rxFileDiagnostic(fileId: $fileId, type: $type, correctedTime: $correctedTime) {
        data
      }
    }
  `;
  const variables = { fileId, type: dataType, correctedTime };
  const res = await callGqlApi(query, variables);
  return res.rxFileDiagnostic;
}
